import React, { useEffect, useState } from "react";
import Footer from "../components/Shared/Footer";
import Navbar from "../components/NavBar/Navbar";
import { useStripe } from "@stripe/react-stripe-js";
import { useOrderContext } from "./../hooks/useContext/useOrderContext";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Grid, Box, CircularProgress, Button, TextField } from "@mui/material";
import { styled } from "@mui/system";
import Divider from "@mui/material/Divider";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useEmail } from "../hooks/useEmail";
import { useTranslation } from "../hooks/useTranslation";
// import { usePrint } from "../hooks/usePrint";


const GridContainerMain = styled("div")({
  width: "100%",
  paddingBottom: "1%",
  minHeight: "100vh",
  border: 0,
  borderRadius: "1px",
  marginTop: "20px",
  paddingLeft: "25%",
  paddingRight: "25%",
  "@media (max-width: 1600px)": {
    paddingLeft: "20%",
    paddingRight: "20%",
  },
  "@media (max-width: 960px)": {
    paddingLeft: "1%",
    paddingRight: "1%",
  },
  "@media (max-width: 600px)": {
    paddingLeft: "1%",
    paddingRight: "1%",
  },
});


function Complete() {
  const stripe = useStripe();
  const { order, name, email, paymentMethod, dispatch, lastOrderId } = useOrderContext();
  const { sendEmail } = useEmail();
  // const { print } = usePrint();
  const [status, setStatus] = useState("");
  // Try to load placedOrder from localStorage if order is empty
  const [placedOrder, setPlacedOrder] = useState(() => {
    if (order && order.length > 0) return order;
    try {
      const stored = localStorage.getItem('placedOrder');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [kitchenStatus, setKitchenStatus] = useState(null);
  const [kitchenStatusLoading, setKitchenStatusLoading] = useState(false);
  const [callToCounterAt, setCallToCounterAt] = useState(null);
  const theme = useTheme();
  const { t, language } = useTranslation();
  const [orderEmail] = useState(email);
  const [orderName] = useState(name);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackSectionVisible, setFeedbackSectionVisible] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [dailyOrderNumber, setDailyOrderNumber] = useState(null);

  const successIcon = <CheckCircleIcon sx={{ fontSize: 50, color: theme.palette.common.success }}/>;
  const errorIcon = <DoDisturbAltIcon sx={{ fontSize: 50, color: theme.palette.common.error }}/>;
  const processingIcon = <AutorenewIcon sx={{ fontSize: 50, color: theme.palette.common.black }}/>;
  const orderNumber = Number.isInteger(dailyOrderNumber)
    ? String(dailyOrderNumber).padStart(3, '0')
    : (lastOrderId ? String(lastOrderId).slice(-6).toUpperCase() : null);

  const STATUS_CONTENT_MAP = {
    succeeded: { text: `${t('thankYouForOrder')}, ${orderName}`, icon: successIcon},
    processing: { text: language === 'pl' ? "Twoja płatność jest przetwarzana." : "Your payment is processing.", icon: processingIcon },
    requires_payment_method: { text: language === 'pl' ? "Twoja płatność nie powiodła się, spróbuj ponownie." : "Your payment was not successful, please try again.", icon: errorIcon},
    default: { text: language === 'pl' ? "Coś poszło nie tak, spróbuj ponownie." : "Something went wrong, please try again.", icon: errorIcon},
  };

  const translateSize = (size) => {
    if (size === 'Small') return t('small');
    if (size === 'Large') return t('large');
    if (size === 'One Size') return t('oneSize');
    return size;
  };

  useEffect(() => {
    if (paymentMethod === 'inPerson' && placedOrder.length === 0) {
      console.log('No order placed. Redirecting...');
      // console.log('1 window.location.href = "/order"');
      window.location.href = "/order";
      return;
    }

    if (paymentMethod === 'inPerson') {
      // console.log('inPerson paymentMethod');
      setStatus("succeeded");
      return;
    } 
    
    if (paymentMethod === 'online') {
      if (placedOrder.length === 0) {
        console.log('2 window.location.href = "/order";');
        window.location.href = "/order";
        return;
      }
      if (!stripe) {
        // console.log('!stripe');
        setIsLoading(true);
        return;
      }
      const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
      if (!clientSecret && paymentMethod === 'online') {
        // console.log('1 window.location.href = "/checkout";');
        window.location.href = "/checkout";
        return;
      }
      stripe.retrievePaymentIntent(clientSecret).then(({paymentIntent}) => {
        if (!paymentIntent && paymentMethod === 'online') {
          console.log("No payment intent found");
          // window.location.href = "/checkout";
          console.log('2 window.location.href = "/checkout";');
          return;
        }
        setStatus(paymentIntent.status);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log("Error retrieving payment intent", error);
        setStatus("default");
        setIsLoading(false);
      });
    }

  }, [stripe, paymentMethod, order, placedOrder.length]);

    useEffect(() => {
      if (order && order.length > 0) {
        if (status === "succeeded") {
          setIsLoading(false);
          setPlacedOrder(order);
          // Store order in localStorage
          try {
            localStorage.setItem('placedOrder', JSON.stringify(order));
          } catch {}
          //only if email is provided
          if (orderEmail) {
            sendEmail(orderEmail, order, orderName, paymentMethod, language, lastOrderId);
          }
          // print(orderEmail, order, orderName, paymentMethod);
          dispatch({ type: 'CLEAR_ORDER', payload: paymentMethod });
        } else if (status === "processing") {
          setIsLoading(true);
          setPlacedOrder([]);
        } else if (status === "requires_payment_method" || status === "detault" ) {
          setPlacedOrder([]);
        }
      }
    }, [order, status, dispatch, paymentMethod, language, orderEmail, orderName, sendEmail, lastOrderId]);


    useEffect(() => {
      if (status !== "succeeded") return;
      if (!lastOrderId) return;

      let timeoutId;
      let isCancelled = false;
      let consecutiveErrors = 0;

      const getNextDelayMs = () => {
        const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
        const baseMs = isHidden ? 60000 : 15000;

        // Backoff on errors up to ~2 minutes.
        const backoffFactor = Math.min(consecutiveErrors, 3);
        const backoffMs = baseMs * Math.pow(2, backoffFactor);

        // Small jitter to avoid thundering herd.
        const jitterMs = Math.floor(Math.random() * 750);
        return Math.min(backoffMs + jitterMs, 120000);
      };

      const fetchStatus = async () => {
        if (isCancelled) return;
        const controller = new AbortController();

        try {
          setKitchenStatusLoading(true);
          const res = await fetch(
            process.env.REACT_APP_SERVER_URL + `/api/orders/status/${lastOrderId}`,
            { signal: controller.signal }
          );
          const json = await res.json();
          if (!res.ok) throw new Error(json?.error || 'Failed to fetch order status');

          consecutiveErrors = 0;
          const nextStatus = json?.status || null;
          if (!isCancelled) setKitchenStatus(nextStatus);
          if (!isCancelled) setCallToCounterAt(json?.callToCounterAt || null);
          if (!isCancelled && Number.isInteger(json?.dailyOrderNumber)) {
            setDailyOrderNumber(json.dailyOrderNumber);
          }
          const existingFeedback = json?.customerFeedback?.text;
          if (!isCancelled && existingFeedback) {
            setFeedbackText(existingFeedback);
            setFeedbackSubmitted(true);
          }

          // Stop polling once ready.
          if (nextStatus === 'Complete') {
            return;
          }
        } catch (e) {
          consecutiveErrors += 1;
        } finally {
          if (!isCancelled) setKitchenStatusLoading(false);
        }

        timeoutId = setTimeout(fetchStatus, getNextDelayMs());
      };

      const onVisibilityChange = () => {
        if (isCancelled) return;
        if (timeoutId) clearTimeout(timeoutId);
        // Re-check quickly when user returns.
        timeoutId = setTimeout(fetchStatus, 250);
      };

      fetchStatus();
      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        isCancelled = true;
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, [lastOrderId, status]);

    const submitFeedback = async () => {
      if (!lastOrderId) return;

      const nextFeedback = String(feedbackText || '').trim();
      if (!nextFeedback) {
        setFeedbackError(t('feedbackRequired'));
        return;
      }

      setFeedbackLoading(true);
      setFeedbackError(null);

      try {
        const response = await fetch(
          process.env.REACT_APP_SERVER_URL + `/api/orders/feedback/${lastOrderId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ text: nextFeedback }),
          }
        );

        const json = await response.json();
        if (!response.ok) {
          throw new Error(json?.error || 'Failed to submit feedback');
        }

        setFeedbackText(json?.customerFeedback?.text || nextFeedback);
        setFeedbackSubmitted(true);
      } catch (error) {
        setFeedbackError(error.message || t('feedbackSubmitFailed'));
      } finally {
        setFeedbackLoading(false);
      }
    };


  return (
    <div style={{backgroundColor: theme.palette.common.background}}>
      <Navbar />
      <GridContainerMain sx={{backgroundColor: theme.palette.common.background}}>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column', marginTop: '50px' }}>
            {status && STATUS_CONTENT_MAP[status] ? STATUS_CONTENT_MAP[status].icon : null}
            <Typography variant="h2" align="center" style={{marginTop: "20px", marginBottom: "20px"}}>
              {status ? STATUS_CONTENT_MAP[status].text : "Loading..."}
            </Typography>
            {status === "succeeded" ? (
              <Typography variant="p" align="center">
                {t('textWhenReady')}
              </Typography>
            ) : null }

            {status === "succeeded" && lastOrderId ? (
              <Box sx={{ mt: 3, width: '100%', display: 'flex', justifyContent: 'center' }}>
                {kitchenStatus === 'Complete' ? (
                  <Paper
                    elevation={3}
                    sx={{
                      width: '100%',
                      maxWidth: 640,
                      p: { xs: 2, sm: 3 },
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.success.main}`,
                      background: `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.common.white} 70%)`,
                      animation: 'readyPulse 1.6s ease-in-out infinite',
                      '@keyframes readyPulse': {
                        '0%': { boxShadow: '0 0 0 rgba(0,0,0,0)' },
                        '50%': { boxShadow: `0 0 0 6px ${theme.palette.success.light}` },
                        '100%': { boxShadow: '0 0 0 rgba(0,0,0,0)' },
                      },
                    }}
                  >
                    <Stack spacing={1} alignItems="center">
                      <CheckCircleIcon sx={{ fontSize: 64, color: theme.palette.success.main }} />
                      <Typography variant="h3" align="center" sx={{ fontWeight: 800 }}>
                        {language === 'pl' ? 'Gotowe do odbioru!' : 'Ready for pickup!'}
                      </Typography>
                      <Typography variant="body1" align="center" sx={{ color: theme.palette.common.grey }}>
                        {language === 'pl'
                          ? 'Podejdź do lady — Twoje zamówienie czeka.'
                          : 'Please come to the counter — your order is waiting.'}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setFeedbackSectionVisible(true)}
                        sx={{ mt: 1 }}
                      >
                        {t('leavePersonalFeedback')}
                      </Button>
                    </Stack>
                  </Paper>
                ) : callToCounterAt ? (
                  <Paper
                    elevation={3}
                    sx={{
                      width: '100%',
                      maxWidth: 640,
                      p: { xs: 2, sm: 3 },
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.warning.main}`,
                      background: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.common.white} 70%)`,
                    }}
                  >
                    <Stack spacing={1} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 64, color: theme.palette.warning.main }} />
                      <Typography variant="h4" align="center" sx={{ fontWeight: 800 }}>
                        {language === 'pl' ? 'Prosimy podejść do lady' : 'Please come to the counter'}
                      </Typography>
                      <Typography variant="body1" align="center" sx={{ color: theme.palette.common.grey }}>
                        {language === 'pl'
                          ? 'Obsługa prosi o kontakt w sprawie Twojego zamówienia.'
                          : 'Staff asked you to come to the counter about your order.'}
                      </Typography>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      width: '100%',
                      maxWidth: 640,
                      p: { xs: 2, sm: 3 },
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.component.borderColor}`,
                      backgroundColor: theme.palette.component.componentBackground,
                    }}
                  >
                    <Stack spacing={1.5} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 44, color: theme.palette.common.offGrey }} />
                      <Typography variant="h5" align="center" sx={{ fontWeight: 700 }}>
                        {language === 'pl' ? 'W przygotowaniu' : 'In progress'}
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ color: theme.palette.common.grey }}>
                        {kitchenStatusLoading
                          ? (language === 'pl' ? 'Sprawdzanie statusu…' : 'Checking status…')
                          : (language === 'pl'
                            ? 'Ta strona zaktualizuje się automatycznie, gdy będzie gotowe.'
                            : 'This page will update automatically when it’s ready.')}
                      </Typography>
                    </Stack>
                  </Paper>
                )}
              </Box>
            ) : null}

            {!status && isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
              </Box>
            )}
          </Grid>
        </Grid>

        {status === "succeeded" && (
        <>
          {status === "succeeded" && orderNumber && (
            <Box
              sx={{
                border: `1px solid ${theme.palette.component.borderColor}`,
                borderRadius: '20px',
                padding: '14px 16px',
                backgroundColor: theme.palette.component.componentBackground,
                margin: '16px 10px 10px 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="h4">
                {t('orderNumber')}: {orderNumber}
              </Typography>

              {feedbackSubmitted && (
                <Button
                  variant="outlined"
                  onClick={() => setShowReview((prev) => !prev)}
                  sx={{ textTransform: 'none' }}
                >
                  {showReview ? t('hideReview') : t('viewReview')}
                </Button>
              )}

              {feedbackSubmitted && showReview && (
                <Box
                  sx={{
                    width: '100%',
                    mt: 0.5,
                    p: 1.5,
                    border: `1px solid ${theme.palette.component.borderColor}`,
                    borderRadius: 1.5,
                  }}
                >
                  <Typography variant="body2">{feedbackText}</Typography>
                </Box>
              )}
            </Box>
          )}

          {kitchenStatus === 'Complete' && feedbackSectionVisible && (
            <Box
              sx={{
                border: `1px solid ${theme.palette.component.borderColor}`,
                borderRadius: '20px',
                padding: '16px',
                backgroundColor: theme.palette.component.componentBackground,
                margin: '20px 10px 10px 10px',
              }}
            >
              <Typography variant="h4" sx={{ mb: 1 }}>
                {t('yourFeedback')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('shareFeedbackDirectly')}
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={4}
                value={feedbackText}
                onChange={(event) => setFeedbackText(event.target.value)}
                disabled={feedbackSubmitted || feedbackLoading}
              />

              {feedbackError && (
                <Typography variant="body2" sx={{ color: theme.palette.error.main, mt: 1 }}>
                  {feedbackError}
                </Typography>
              )}

              {feedbackSubmitted && (
                <Typography variant="body2" sx={{ color: theme.palette.success.main, mt: 1 }}>
                  {t('feedbackThanks')}
                </Typography>
              )}

              {!feedbackSubmitted && (
                <Button
                  variant="primaryButton"
                  onClick={submitFeedback}
                  disabled={feedbackLoading}
                  sx={{ mt: 2 }}
                >
                  {feedbackLoading
                    ? (language === 'pl' ? 'Wysyłanie…' : 'Submitting…')
                    : t('submitFeedback')}
                </Button>
              )}
            </Box>
          )}

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: "10px 0" }}>
            <Typography variant="p" style={{fontWeight: 600}}>
              {t('paymentStatus')}
            </Typography>
            <Typography variant="body2" style={{ marginLeft: "10px" }}>
              {paymentMethod === 'online' ? t('paid') : (language === 'pl' ? "Płatność na miejscu" : "Pay in Person")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h3" align="center" style={{ marginTop: "50px", marginBottom: "10px" }}>
              {t('orderSummary')}
            </Typography>
          </Grid>
          <Box sx={{ border: `1px solid ${theme.palette.component.borderColor}`, borderRadius: "20px", padding: "10px", backgroundColor: theme.palette.component.componentBackground, margin: "0 10px 0 10px" }}>
            <Grid container spacing={2}>
              {placedOrder.map((cartItem, index) => (
                <Grid item key={index} xs={12}>
                  <Grid container direction="row" spacing={0} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid item xs={10} sm={10} md={10} lg={10} xl={10} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'left', padding: "10px 0 10px 10px", flexDirection: 'column' }}>
                      <Typography variant="p" gutterBottom sx={{ textAlign: "left"}}>
                        {language === 'pl' ? (t(cartItem.itemName) || cartItem.itemName) : cartItem.itemName}
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: "left", paddingLeft: '10px' }}>
                        {`Qty: ${cartItem.itemQuantity} - (${translateSize(cartItem.itemSize)})`}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sm={2} md={2} lg={2} xl={2} sx={{textAlign: 'center'}}>
                      <Typography variant="p">
                        {`${(cartItem.itemQuantity * cartItem.itemPrice)} ${t('currency')}`}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider variant="middle" />
                </Grid>
              ))}
              <Divider variant="middle"/>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: "10px" }}>
                <Typography variant="h4" sx={{marginLeft: "20px"}}>
                  {t('total')}
                </Typography>
                <Typography variant="h4" sx={{marginRight: "10px"}}>
                  {`${placedOrder.reduce((acc, item) => acc + item.itemQuantity * item.itemPrice, 0)} ${t('currency')}`}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
        )}

        {status === "requires_payment_method" || status === "default" ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: "10px 0 10px 10px", flexDirection: 'column' }}>
              <Typography variant="h6" align="center" style={{ marginTop: "20px" }}>
                Prefer to pay in person?
              </Typography>
              <Typography variant="body2" align="center" style={{ marginBottom: "20px"}}>
                Please order at the front counter.
              </Typography>
              <Button variant="greyButton" href="/cart" sx={{minWidth: "200px", marginBottom: "20px", backgroundColor: theme.palette.common.offWhite, borderColor: theme.palette.component.borderColor}}>
                {t('backToCart')}
              </Button>
              <Button variant="primaryButton" href="/checkout" sx={{minWidth: "200px"}}>
                {t('backToCheckout')}
              </Button>
            </Grid>
          </Grid>
        ) : null}

      </GridContainerMain>
      <Footer />
    </div>
  );
}

export default Complete;